import React, { useState, useEffect, lazy, Suspense, useRef } from "react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Globe,
  Package,
  Thermometer,
  Leaf,
  Award,
  Shield,
  TrendingUp,
  Truck,
  Users,
  Heart,
  MapPin,
  Phone,
  Mail,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  ChevronRight,
  Star,
  CheckCircle2,
  Factory,
} from "lucide-react";
import WhatsAppButton from "../components/WhatsAppButton";
const CountryMap = lazy(() => import("../components/CountryMap"));
const Footer = lazy(() => import("@/components/Footer"));
const Navbar = lazy(() => import("@/components/Navbar"));
import useScrollToSection from "@/hooks/useScrollToSection";
import img1 from "../assets/img1.jpeg";
import img2 from "../assets/img2.jpeg";
import img3 from "../assets/img3.jpeg";
import img4 from "../assets/img4.jpeg";
import orange from "../assets/orange.jpg";
import pineapple from "../assets/pineapple.jpg";
import grapes from "../assets/grapes.jpg";
import dragon from "../assets/dragonfruits.jpg";
import banana from "../assets/banana.jpeg";
import apple from "../assets/apple.jpeg";

const Landing = () => {
  const [isVisible, setIsVisible] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [stats, setStats] = useState({
    countries: 0,
    years: 0,
    tons: 0,
    farmers: 0,
  });
  const statsRef = useRef(stats);

  // Scroll to section on nav link click
  useScrollToSection();

  // Animated counter effect
  useEffect(() => {
    const target = { countries: 8, years: 5, tons: 50000, farmers: 5000 };
    const duration = 2000;
    const steps = 30; // reduce steps = fewer renders
    const interval = duration / steps;

    let step = 0;

    const timer = setInterval(() => {
      step++;
      const p = step / steps;

      const newStats = {
        countries: Math.floor(target.countries * p),
        years: Math.floor(target.years * p),
        tons: Math.floor(target.tons * p),
        farmers: Math.floor(target.farmers * p),
      };

      statsRef.current = newStats;
      setStats(newStats);

      if (step >= steps) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, []);

  // Scroll reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 },
    );

    document
      .querySelectorAll('[id^="section-"]')
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    alert("Thank you! We will contact you soon.");
    setFormData({ name: "", email: "", phone: "", message: "" });
  };

  //scroll to section
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1619566636858-adf3ef46400b?auto=format&fit=crop&w=1400&q=80"
            alt="Farm landscape"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 to-emerald-700/60"></div>
        </div>

        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up">
            Farm to Market
          </h1>
          <p
            className="text-xl md:text-2xl mb-8 text-gray-100 animate-fade-in-up"
            style={{ animationDelay: "0.2s" }}
          >
            Premium Agro Exports Connecting Indian Farmers to Global Markets
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up"
            style={{ animationDelay: "0.4s" }}
          >
            <Button
              size="lg"
              className="bg-white text-emerald-800 hover:bg-gray-100 shadow-lg"
              onClick={() => {
                scrollToSection("section-services");
              }}
            >
              Explore Products <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => {
                scrollToSection("section-contact");
              }}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-emerald-700 mb-2">
                {stats.countries}+
              </div>
              <div className="text-gray-600 font-medium">
                Countries Exported
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-amber-600 mb-2">
                {stats.years}+
              </div>
              <div className="text-gray-600 font-medium">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-emerald-700 mb-2">
                {stats.tons.toLocaleString()}+
              </div>
              <div className="text-gray-600 font-medium">Tons Exported</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-amber-600 mb-2">
                {stats.farmers.toLocaleString()}+
              </div>
              <div className="text-gray-600 font-medium">Farmers Supported</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="section-about"
        className={`py-20 bg-gradient-to-br from-stone-50 to-amber-50 scroll-reveal ${isVisible["section-about"] ? "is-visible" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Empowering Farmers,
                <br />
                Feeding the World
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                We are a leading Indian agro export company specializing in
                premium quality bananas and fresh fruits. Our mission is to
                connect local farmers with global markets while ensuring
                sustainable farming practices and fair trade.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  <span className="text-gray-700 font-medium">
                    Quality Certified
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  <span className="text-gray-700 font-medium">
                    Global Compliance
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  <span className="text-gray-700 font-medium">
                    Sustainable Practices
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1552901633-210088e17486?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxiYW5hbmElMjBwbGFudGF0aW9ufGVufDB8fHx8MTc3OTM3MjU3N3ww&ixlib=rb-4.1.0&q=85"
                alt="Banana plantation"
                loading="lazy"
                decoding="async"
                className="rounded-lg shadow-lg w-full h-48 object-cover"
              />
              <img
                src={apple}
                alt="Farm"
                loading="lazy"
                decoding="async"
                className="rounded-lg shadow-lg w-full h-48 object-cover mt-8"
              />
              <img
                src="https://images.pexels.com/photos/6012718/pexels-photo-6012718.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Agriculture"
                loading="lazy"
                decoding="async"
                className="rounded-lg shadow-lg w-full h-48 object-cover"
              />
              <img
                src={banana}
                alt="Modern farming"
                loading="lazy"
                decoding="async"
                className="rounded-lg shadow-lg w-full h-48 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="section-services"
        className={`py-20 bg-white scroll-reveal ${isVisible["section-services"] ? "is-visible" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive agricultural solutions from farm to global markets
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-t-4 border-t-emerald-600">
              <CardHeader>
                <Globe className="h-12 w-12 text-emerald-600 mb-4" />
                <CardTitle className="text-xl">Global Exports</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Exporting premium agricultural products to Middle East,
                  Europe, and Asia with full compliance and documentation.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-t-4 border-t-amber-600">
              <CardHeader>
                <TrendingUp className="h-12 w-12 text-amber-600 mb-4" />
                <CardTitle className="text-xl">Strategic Imports</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Sourcing quality agricultural inputs and specialty products
                  for domestic markets.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-t-4 border-t-amber-600">
              <CardHeader>
                <Thermometer className="h-12 w-12 text-amber-600 mb-4" />
                <CardTitle className="text-xl">Cold Chain Logistics</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Advanced temperature-controlled storage and transportation
                  ensuring product freshness.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-t-4 border-t-amber-600">
              <CardHeader>
                <Package className="h-12 w-12 text-amber-600 mb-4" />
                <CardTitle className="text-xl">Packaging Solutions</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Export-grade packaging ensuring product safety during
                  international transit.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Export Countries Section */}
      <section
        id="section-countries"
        className={`py-20 bg-gradient-to-br from-emerald-900 to-emerald-700 text-white scroll-reveal ${
          isVisible["section-countries"] ? "is-visible" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Suspense fallback={<div>Loading...</div>}>
            <CountryMap />
          </Suspense>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-12">
            {[
              "Jammu & Kashmir",
              "Delhi",
              "Punjab",
              "Haryana",
              "Uttar Pradesh",
              "Himachal Pradesh",
              "UAE",
              "Saudi Arabia",
              "Qatar",
              "Oman",
              "Kuwait",
              "Bahrain",
              "Jordan",
              "Egypt",
            ].map((state) => (
              <div
                key={state}
                className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-colors"
              >
                <MapPin className="h-5 w-5 text-amber-400" />
                <span className="font-medium">{state}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section
        id="section-why"
        className={`py-20 bg-white scroll-reveal ${isVisible["section-why"] ? "is-visible" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Us
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Excellence in every aspect of agricultural exports
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                <Award className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Quality Assurance
              </h3>
              <p className="text-gray-600">
                Rigorous quality checks at every stage ensuring only premium
                products reach global markets.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                <Shield className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Global Compliance
              </h3>
              <p className="text-gray-600">
                Full adherence to international food safety standards and export
                regulations.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                <Thermometer className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Cold Chain Innovation
              </h3>
              <p className="text-gray-600">
                Advanced temperature-controlled logistics maintaining product
                freshness from farm to destination.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                <Truck className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Fast Delivery
              </h3>
              <p className="text-gray-600">
                Efficient supply chain management ensuring timely delivery to
                global markets.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                <Leaf className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Ethical Farming
              </h3>
              <p className="text-gray-600">
                Promoting sustainable and environmentally responsible farming
                practices.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                <Heart className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Rural Empowerment
              </h3>
              <p className="text-gray-600">
                Supporting local farmers with fair prices and modern
                agricultural techniques.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Processing Section */}
      <section
        id="section-processing"
        className={`py-20 bg-gradient-to-br from-stone-800 to-stone-900 text-white scroll-reveal ${isVisible["section-processing"] ? "is-visible" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                World-Class Processing
              </h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Our state-of-the-art processing facilities employ cutting-edge
                technology and stringent quality controls to deliver premium
                products.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <Factory className="h-6 w-6 text-amber-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">
                      Modern Infrastructure
                    </h4>
                    <p className="text-gray-400">
                      Automated processing lines with food-grade equipment
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Users className="h-6 w-6 text-amber-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">Women Empowerment</h4>
                    <p className="text-gray-400">
                      Creating employment opportunities for rural women
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Shield className="h-6 w-6 text-amber-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">Quality Control</h4>
                    <p className="text-gray-400">
                      ISO certified processes ensuring consistent quality
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src={img1}
                alt="Processing"
                loading="lazy"
                decoding="async"
                className="rounded-lg shadow-lg w-full h-40 object-cover"
              />
              <img
                src={img2}
                alt="Warehouse"
                loading="lazy"
                decoding="async"
                className="rounded-lg shadow-lg w-full h-40 object-cover mt-8"
              />
              <img
                src={img3}
                alt="Quality control"
                loading="lazy"
                decoding="async"
                className="rounded-lg shadow-lg w-full h-40 object-cover"
              />
              <img
                src={img4}
                alt="Technology"
                loading="lazy"
                decoding="async"
                className="rounded-lg shadow-lg w-full h-40 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How We Work
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We connect farmers directly to global markets through strict
              quality control, professional packaging, and reliable delivery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <Card className="hover:shadow-xl transition-all duration-300 border-t-4 border-t-emerald-600">
              <CardHeader>
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <Users className="h-7 w-7 text-emerald-700" />
                </div>
                <CardTitle>Farmer Connection</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  We connect local farmers with domestic and international
                  buyers, helping them access larger markets and better
                  opportunities.
                </CardDescription>
              </CardContent>
            </Card>

            {/* Step 2 */}
            <Card className="hover:shadow-xl transition-all duration-300 border-t-4 border-t-amber-600">
              <CardHeader>
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                  <Shield className="h-7 w-7 text-amber-700" />
                </div>
                <CardTitle>Quality Inspection</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Our quality experts personally visit farms and inspect
                  products to ensure freshness, safety, and export-grade
                  standards.
                </CardDescription>
              </CardContent>
            </Card>

            {/* Step 3 */}
            <Card className="hover:shadow-xl transition-all duration-300 border-t-4 border-t-emerald-600">
              <CardHeader>
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <Package className="h-7 w-7 text-emerald-700" />
                </div>
                <CardTitle>Custom Packaging</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Products are packed directly at farms or processing centers
                  based on buyer requirements and international shipping
                  standards.
                </CardDescription>
              </CardContent>
            </Card>

            {/* Step 4 */}
            <Card className="hover:shadow-xl transition-all duration-300 border-t-4 border-t-amber-600">
              <CardHeader>
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                  <Truck className="h-7 w-7 text-amber-700" />
                </div>
                <CardTitle>Delivery & Supply</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  We handle transportation, export documentation, and timely
                  delivery to buyers across domestic and international markets.
                </CardDescription>
              </CardContent>
            </Card>
          </div>

          {/* Bottom Features */}
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-md p-6 flex items-start space-x-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  Farm to Market
                </h4>
                <p className="text-gray-600 text-sm">
                  Direct sourcing from trusted farms to local and global buyers.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 flex items-start space-x-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  Domestic & Export Supply
                </h4>
                <p className="text-gray-600 text-sm">
                  Supplying products across India and international markets.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 flex items-start space-x-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  Trusted Quality
                </h4>
                <p className="text-gray-600 text-sm">
                  Every shipment passes strict quality inspection before
                  dispatch.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section
        id="section-products"
        className={`py-20 bg-gradient-to-br from-amber-50 to-stone-50 scroll-reveal ${isVisible["section-products"] ? "is-visible" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Premium Products
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Export-quality agricultural products meeting international
              standards
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* banana */}
            <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <img
                src={banana}
                alt="Bananas"
                loading="lazy"
                decoding="async"
                className="w-full h-48 object-cover"
              />
              <CardHeader>
                <CardTitle>Fresh Bananas</CardTitle>
                <CardDescription>
                  Naturally ripened export-quality bananas with rich taste
                </CardDescription>
              </CardHeader>
            </Card>
            {/* apple */}
            <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <img
                src={apple}
                alt="Apples"
                loading="lazy"
                decoding="async"
                className="w-full h-48 object-cover"
              />
              <CardHeader>
                <CardTitle>Fresh Apples</CardTitle>
                <CardDescription>
                  Premium quality fresh apples sourced from trusted farms
                </CardDescription>
              </CardHeader>
            </Card>
            {/* Dragon Fruit */}
            <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <img
                src={dragon}
                alt="Dragon Fruit"
                loading="lazy"
                decoding="async"
                className="w-full h-48 object-cover"
              />
              <CardHeader>
                <CardTitle>Premium Dragon Fruit</CardTitle>
                <CardDescription>
                  Fresh and nutritious dragon fruits packed with natural
                  goodness
                </CardDescription>
              </CardHeader>
            </Card>
            {/* Orange */}
            <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <img
                src={orange}
                alt="Oranges"
                loading="lazy"
                decoding="async"
                className="w-full h-48 object-cover"
              />
              <CardHeader>
                <CardTitle>Fresh Oranges</CardTitle>
                <CardDescription>
                  Juicy and naturally sweet oranges with premium freshness
                </CardDescription>
              </CardHeader>
            </Card>
            {/* Grapes */}
            <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <img
                src={grapes}
                alt="Grapes"
                loading="lazy"
                decoding="async"
                className="w-full h-48 object-cover"
              />
              <CardHeader>
                <CardTitle>Fresh Grapes</CardTitle>
                <CardDescription>
                  High-quality green and black grapes for domestic supply
                </CardDescription>
              </CardHeader>
            </Card>
            {/* Pineapple  */}
            <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <img
                src={pineapple}
                alt="Pineapple"
                loading="lazy"
                decoding="async"
                className="w-full h-48 object-cover"
              />
              <CardHeader>
                <CardTitle>Fresh Pineapple</CardTitle>
                <CardDescription>
                  Farm-fresh pineapples with tropical sweetness and freshness
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        id="section-testimonials"
        className={`py-20 bg-gradient-to-br from-emerald-50 to-amber-50 scroll-reveal ${
          isVisible["section-testimonials"] ? "is-visible" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Client Testimonials
            </h2>

            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              हम पर भरोसा करते हैं किसान और व्यापारी — India’s trusted agro
              network
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* TESTIMONIAL 1 */}
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-5 w-5 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>

                <CardDescription className="text-base text-gray-700 italic">
                  "बहुत अच्छी quality के फल और समय पर delivery। उनकी supply
                  chain service बहुत reliable है। Highly recommended!"
                </CardDescription>
              </CardHeader>

              <CardContent>
                <p className="font-semibold text-gray-900">Amit Verma</p>
                <p className="text-sm text-gray-600">
                  Retail Buyer, Delhi, India
                </p>
              </CardContent>
            </Card>

            {/* TESTIMONIAL 2 */}
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-5 w-5 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>

                <CardDescription className="text-base text-gray-700 italic">
                  "इनसे जुड़ने के बाद हमारी खेती की income बढ़ गई है। Training
                  और support बहुत अच्छा मिलता है। धन्यवाद!"
                </CardDescription>
              </CardHeader>

              <CardContent>
                <p className="font-semibold text-gray-900">Ramesh Gowda</p>
                <p className="text-sm text-gray-600">
                  Farmer, Karnataka, India
                </p>
              </CardContent>
            </Card>

            {/* TESTIMONIAL 3 */}
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-5 w-5 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>

                <CardDescription className="text-base text-gray-700 italic">
                  "Export quality fruits at very fair prices. Delivery always on
                  time. Professional team and excellent service."
                </CardDescription>
              </CardHeader>

              <CardContent>
                <p className="font-semibold text-gray-900">Neha Singh</p>
                <p className="text-sm text-gray-600">
                  Wholesale Trader, Uttar Pradesh, India
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="section-contact"
        className={`py-20 bg-white scroll-reveal ${isVisible["section-contact"] ? "is-visible" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Get In Touch
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Ready to partner with us? Contact our team for inquiries about
                exports, imports, or farmer support programs.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Head Office
                    </h4>
                    <p className="text-gray-600">
                      Shop No 3, Dharam Weight Bridge, Infront of Nayara Petrol
                      Pump, Walgaon Road, Amravati, Maharashtra, India
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Phone className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                    <p className="text-gray-600">+91 76660 68581</p>
                  </div>
                </div>
                {/* <div className="flex items-start space-x-4">
                  <Mail className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <p className="text-gray-600"></p>
                  </div>
                </div> */}
              </div>

              <div className="mt-8">
                <h4 className="font-semibold text-gray-900 mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                  {/* <a
                    href="#"
                    className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center hover:bg-emerald-200 transition-colors"
                  >
                    <Facebook className="h-5 w-5 text-emerald-700" />
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center hover:bg-emerald-200 transition-colors"
                  >
                    <Twitter className="h-5 w-5 text-emerald-700" />
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center hover:bg-emerald-200 transition-colors"
                  >
                    <Instagram className="h-5 w-5 text-emerald-700" />
                  </a> */}
                  <a
                    href="https://www.linkedin.com/in/mohd-uzair-mujiboodin-301687141/"
                    className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center hover:bg-emerald-200 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Linkedin className="h-5 w-5 text-emerald-700" />
                  </a>
                </div>
              </div>
            </div>

            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>Send us a message</CardTitle>
                <CardDescription>
                  We'll get back to you within 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Input
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="Your Message"
                      rows={4}
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                  >
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <WhatsAppButton />
    </div>
  );
};

export default Landing;
